import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { useEffect } from 'react'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { PageTransition } from '@/components/layout/PageTransition'
import { ScrollToTop } from '@/components/layout/ScrollToTop'
import { WaveDashedLine } from '@/components/layout/WaveDashedLine'
import { CartDrawer } from '@/components/CartDrawer'
import { AuthModal } from '@/components/auth/AuthModal'
import { useCartHydration } from '@/hooks/useCartHydration'
import { ToastContainer } from '@/components/ui/Toast'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'

import { Home } from '@/pages/Home'
import { Shop } from '@/pages/Shop'
import { ProductDetail } from '@/pages/ProductDetail'
import { Collections } from '@/pages/Collections'
import { CollectionDetail } from '@/pages/CollectionDetail'
import { ScentQuizPage } from '@/pages/ScentQuizPage'
import { Journal } from '@/pages/Journal'
import { JournalArticle } from '@/pages/JournalArticle'
import { About } from '@/pages/About'
import { Wishlist } from '@/pages/Wishlist'
import { AccountDashboard } from '@/pages/AccountDashboard'
import { LoyaltyPage } from '@/pages/LoyaltyPage'
import { AccountOrdersPage } from '@/pages/AccountOrdersPage'
import { AccountAddressesPage } from '@/pages/AccountAddressesPage'
import { AccountDetailsPage } from '@/pages/AccountDetailsPage'
import { AccountPreferencesPage } from '@/pages/AccountPreferencesPage'
import { Cart } from '@/pages/Cart'
import { Checkout } from '@/pages/Checkout'
import { OrderStatus } from '@/pages/OrderStatus'
import { Privacy } from '@/pages/Privacy'
import { Terms } from '@/pages/Terms'
import { NotFound } from '@/pages/NotFound'

function AnimatedRoutes() {
  const location = useLocation()

  return (
    <AnimatePresence mode="wait">
      <PageTransition key={location.pathname}>
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/collections/:slug" element={<CollectionDetail />} />
          <Route path="/scent-quiz" element={<ScentQuizPage />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<JournalArticle />} />
          <Route path="/about" element={<About />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/account" element={<Navigate to="/account/dashboard" replace />} />
          <Route path="/account/dashboard" element={<AccountDashboard />} />
          <Route path="/account/loyalty" element={<LoyaltyPage />} />
          <Route path="/account/orders" element={<AccountOrdersPage />} />
          <Route path="/account/addresses" element={<AccountAddressesPage />} />
          <Route path="/account/details" element={<AccountDetailsPage />} />
          <Route path="/account/preferences" element={<AccountPreferencesPage />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-status" element={<OrderStatus />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </PageTransition>
    </AnimatePresence>
  )
}

export default function App() {
  useCartHydration()
  const setMotionReduced = useUIStore((s) => s.setMotionReduced)
  const fetchUser = useAuthStore((s) => s.fetchUser)

  // Verify the session cookie on every load. Without this the app only learns
  // it is signed in after visiting the account area, so a returning member is
  // shown as a guest everywhere else on the site.
  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    setMotionReduced(mql.matches)
    const onChange = (e: MediaQueryListEvent) => setMotionReduced(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [setMotionReduced])

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <div className="flex min-h-screen flex-col">
          <ScrollToTop />
          <Header />
          <main className="flex-1">
            <AnimatedRoutes />
          </main>
          <Footer />
          <WaveDashedLine />
          <CartDrawer />
          <AuthModal />
          <ToastContainer />
        </div>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
